import { FirebaseError, type FirebaseApp } from "firebase/app";
import {
  browserLocalPersistence,
  browserPopupRedirectResolver,
  browserSessionPersistence,
  indexedDBLocalPersistence,
  initializeAuth,
  type PopupRedirectResolver,
} from "firebase/auth";

// On mobile and Safari, Firebase's first call here loads Google's gapi iframe
// before the first auth state; if apis.google.com stalls, the app sits on
// "Loading..." forever. Cap that boot wait, and once it has timed out, cap
// sign-in taps too so the form can recover instead of spinning.
export const RESOLVER_INIT_TIMEOUT_MS = 3000;
export const SIGN_IN_INIT_TIMEOUT_MS = 15000;

type Resolver = new () => {
  readonly _shouldInitProactively: boolean;
  _initialize(auth: unknown): Promise<unknown>;
};

const withTimeout = (call: Promise<unknown>, ms: number) => {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<never>((_, reject) => {
    timer = setTimeout(
      () =>
        reject(
          new FirebaseError(
            "auth/network-request-failed",
            "Firebase: Error (auth/network-request-failed).",
          ),
        ),
      ms,
    );
  });
  return Promise.race([call, timeout]).finally(() => clearTimeout(timer));
};

export const withInitTimeout = (Base: Resolver) =>
  class extends Base {
    bootWaitCapped = false;
    bootTimedOut = false;

    _initialize(auth: unknown): Promise<unknown> {
      if (!this._shouldInitProactively) return super._initialize(auth);
      if (this.bootWaitCapped)
        return this.bootTimedOut
          ? withTimeout(super._initialize(auth), SIGN_IN_INIT_TIMEOUT_MS)
          : super._initialize(auth);
      this.bootWaitCapped = true;
      return withTimeout(
        super._initialize(auth),
        RESOLVER_INIT_TIMEOUT_MS,
      ).catch((error) => {
        this.bootTimedOut = true;
        throw error;
      });
    }
  };

// getAuth()'s persistence (minus its emulator auto-connect and
// authTokenSyncURL), with the bounded resolver. Must run before any getAuth().
export const initAuth = (app: FirebaseApp) =>
  initializeAuth(app, {
    persistence: [
      indexedDBLocalPersistence,
      browserLocalPersistence,
      browserSessionPersistence,
    ],
    popupRedirectResolver: withInitTimeout(
      browserPopupRedirectResolver as unknown as Resolver,
    ) as unknown as PopupRedirectResolver,
  });
